import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "../../../components/base/Modal";
import type { AcPass } from "../api/accessControlApi";
import { PASS_TONE, StatusChip, toneOf } from "./console/Console";

// ===========================================================================
// Invitación compartible de un acceso temporal: QR descargable, tarjeta PNG
// con los datos de la visita, mensaje para copiar y envío por WhatsApp o por
// el menú de compartir del sistema.
// ===========================================================================

const fmt = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));

export function invitationText(p: AcPass, t: (k: string, o?: Record<string, unknown>) => string, locale: string): string {
  return t("invitation.message", {
    visitor: p.visitor_name,
    host: p.host_name,
    site: p.site_name ?? "",
    from: fmt(p.valid_from, locale),
    until: fmt(p.valid_until, locale),
    code: p.code,
    uses: p.max_uses,
    interpolation: { escapeValue: false },
  });
}

const brandColor = () => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue("--brand-primary").trim() || "#0099f5";
  } catch {
    return "#0099f5";
  }
};

/** Tarjeta de invitación en PNG (1080×1350). */
export async function renderInvitationPng(p: AcPass, qrDataUrl: string, labels: Record<string, string>, locale: string): Promise<string> {
  const W = 1080;
  const H = 1350;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = brandColor();
  ctx.fillRect(0, 0, W, 210);
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 54px system-ui, sans-serif";
  ctx.fillText(labels.title, 70, 120);
  ctx.font = "400 30px system-ui, sans-serif";
  ctx.fillText(p.site_name ?? "", 70, 170);

  const img = new Image();
  img.src = qrDataUrl;
  await img.decode().catch(() => undefined);
  ctx.drawImage(img, (W - 560) / 2, 260, 560, 560);
  ctx.fillStyle = "#111827";
  ctx.font = "700 46px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.fillText(p.code, W / 2, 880);
  ctx.textAlign = "left";

  const rows: [string, string][] = [
    [labels.visitor, `${p.visitor_name}${p.visitor_company ? ` · ${p.visitor_company}` : ""}`],
    [labels.host, `${p.host_name}${p.host_unit ? ` · ${p.host_unit}` : ""}`],
    [labels.validity, `${fmt(p.valid_from, locale)} → ${fmt(p.valid_until, locale)}`],
    [labels.uses, String(p.max_uses)],
  ];
  let y = 970;
  for (const [k, v] of rows) {
    ctx.fillStyle = "#6b7280";
    ctx.font = "500 26px system-ui, sans-serif";
    ctx.fillText(k, 70, y);
    ctx.fillStyle = "#111827";
    ctx.font = "600 32px system-ui, sans-serif";
    ctx.fillText(v.length > 52 ? `${v.slice(0, 51)}…` : v, 70, y + 40);
    y += 88;
  }
  ctx.fillStyle = "#9ca3af";
  ctx.font = "400 22px system-ui, sans-serif";
  ctx.fillText(labels.footer, 70, H - 40);
  return c.toDataURL("image/png");
}

function download(dataUrl: string, name: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name;
  a.click();
}

export default function PassInvitation({ pass, onClose }: { pass: AcPass; onClose: () => void }) {
  const { t, i18n } = useTranslation("accessConsole");
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    QRCode.toDataURL(pass.qr_token, { width: 640, margin: 1 }).then((url) => {
      if (!cancel) setQr(url);
    });
    return () => {
      cancel = true;
    };
  }, [pass.qr_token]);

  const text = invitationText(pass, t, i18n.language);
  const labels = {
    title: t("invitation.title"),
    visitor: t("fields.visitor"),
    host: t("fields.host"),
    validity: t("fields.validity"),
    uses: t("fields.maxUses"),
    footer: t("invitation.footer"),
  };

  const downloadCard = async () => {
    if (!qr) return;
    const png = await renderInvitationPng(pass, qr, labels, i18n.language);
    if (png) download(png, `invitacion-${pass.code}.png`);
  };

  const share = async () => {
    try {
      if (qr && navigator.share) {
        const png = await renderInvitationPng(pass, qr, labels, i18n.language);
        const blob = await (await fetch(png)).blob();
        const file = new File([blob], `invitacion-${pass.code}.png`, { type: "image/png" });
        const data: ShareData = { title: labels.title, text };
        if (navigator.canShare?.({ files: [file] })) data.files = [file];
        await navigator.share(data);
        return;
      }
    } catch {
      /* cancelado por el usuario: no pasa nada */
      return;
    }
    await copy();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("invitation.copied"));
    } catch {
      toast.error(t("invitation.copyError"));
    }
  };

  const phone = (pass.visitor_phone ?? "").replace(/\D/g, "");
  const wa = `https://wa.me/${phone.length >= 10 ? phone : ""}?text=${encodeURIComponent(text)}`;

  return (
    <Modal
      title={t("invitation.title")}
      onClose={onClose}
      width="max-w-lg w-full"
      disableCRUD
      content={
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-2 rounded-xl border border-line bg-white p-4">
            {qr ? <img src={qr} alt={t("invitation.qrAlt", { interpolation: { escapeValue: false }, code: pass.code })} className="h-56 w-56" /> : <div className="h-56 w-56 animate-pulse rounded bg-gray-100" />}
            <span className="font-mono text-lg font-bold tracking-wider text-gray-900">{pass.code}</span>
            <StatusChip tone={toneOf(PASS_TONE, pass.effective_status)}>{t(`passStatus.${pass.effective_status}`)}</StatusChip>
          </div>
          <pre className="whitespace-pre-wrap rounded-lg border border-line bg-app p-3 font-sans text-sm text-content">{text}</pre>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => qr && download(qr, `qr-${pass.code}.png`)} className="flex items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-sm text-content hover:bg-subtle"><i className="ri-qr-code-line" />{t("invitation.downloadQr")}</button>
            <button type="button" onClick={downloadCard} className="flex items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-sm text-content hover:bg-subtle"><i className="ri-image-line" />{t("invitation.downloadCard")}</button>
            <button type="button" onClick={copy} className="flex items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-sm text-content hover:bg-subtle"><i className="ri-file-copy-line" />{t("invitation.copy")}</button>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg bg-success px-3 py-2 text-sm font-medium text-white hover:brightness-95"><i className="ri-whatsapp-line" />WhatsApp</a>
            <button type="button" onClick={share} className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"><i className="ri-share-forward-line" />{t("invitation.share")}</button>
          </div>
        </div>
      }
    />
  );
}
